/**
 * ワークスペースのタブ状態（開いているファイル一覧・アクティブファイル）を
 * user_profile（AUTO・profile_key="workspace-tabs"）に保存・取得する薄いアダプタ。
 *
 * 旧実装は localStorage 保存だったが、user_profile（サーバ保存）へ移行した。
 * これによりマイワークスペースのタブ状態が端末をまたいで復元される
 * （「最後の状態を復元する」目的に合致）。
 *
 * マイワークスペース専用（他人のワークスペース閲覧時は使用しない）。
 * 呼び出し側の関数名を維持するため、旧アダプタと同名の関数を提供する。
 */
import { parseProfileValue } from 'utils/UserProfileGuard';

import { listUserProfile, saveUserProfile } from '../UserProfileStoreAdapter';

/** user_profile の profile_key */
const PROFILE_KEY = 'workspace-tabs';

/** 保存するタブ状態 */
export type WorkspaceTabsState = {
  /** 開いているファイルの fileId 一覧（ガイドタブなどの擬似タブは含めない） */
  openFileIds: string[];
  /** アクティブなタブの識別子（fileId または擬似タブ ID） */
  activeId: string | null;
};

/** タブ状態を保存する（user_profile AUTO へ upsert）。失敗は握りつぶす */
export const saveWorkspaceTabs = async (state: WorkspaceTabsState): Promise<void> => {
  try {
    await saveUserProfile({ profileKey: PROFILE_KEY, profileValue: JSON.stringify(state) });
  } catch {
    // 保存失敗は UX を止めない（握りつぶす）
  }
};

/** タブ状態を取得する（無ければ null）。失敗・未保存は null */
export const loadWorkspaceTabs = async (): Promise<WorkspaceTabsState | null> => {
  try {
    const results = await listUserProfile(PROFILE_KEY, 'AUTO');
    if (results.length === 0) return null;
    const parsed = parseProfileValue<Partial<WorkspaceTabsState> | null>(
      results[0].profileValue,
      null,
    );
    if (!parsed || !Array.isArray(parsed.openFileIds)) return null;
    return {
      openFileIds: parsed.openFileIds.filter((id): id is string => typeof id === 'string'),
      activeId: typeof parsed.activeId === 'string' ? parsed.activeId : null,
    };
  } catch {
    return null;
  }
};
